import React from 'react';
import styled, { css } from 'styled-components';
import { Body } from '../../../../../../../Typography';
import useLocale from '../../../../../../../../hooks/useLocale';
import Icon from '../../../../../../../Icon';
import JustificationContainer from '../../../../../../../JustificationContainer';
import { DHApp } from '../../../../../../../../shared/types';

export type Props = {
  amountOfOther: number;
  activeSlide: string;
  slides?: Array<DHApp>;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
};

const text = {
  prefix: {
    nl: '...bekijk',
    en: '...check out',
  },
  postfix: {
    nl: 'andere opties',
    en: 'other services',
  },
};

const Footer: React.FC<Props> = ({
  amountOfOther,
  slides,
  activeSlide,
  onClick,
}) => {
  const locale = useLocale();

  return (
    <>
      <Container>
        {(slides?.length ?? 0) > 1 && (
          <IndicatorContainer>
            {slides?.map(({ slug }, index) => {
              const id = `slide-${index + 1}`;
              return (
                <ScrollIndicator
                  key={slug}
                  href={`#${id}`}
                  $active={activeSlide === id}
                />
              );
            })}
          </IndicatorContainer>
        )}
        {amountOfOther !== 0 && (
          <>
            <JustificationContainer
              align="center"
              justification="start"
              gap="xxs"
              onClick={onClick}
            >
              <JustificationContainer
                gap="xxxs"
                justification="start"
                align="center"
              >
                <Body withoutMargin>{text.prefix[locale]}</Body>
                <AmountContainer justification="center" align="center">
                  {amountOfOther}
                </AmountContainer>
                <Body withoutMargin>{text.postfix[locale]}</Body>
              </JustificationContainer>
              <IconContainer>
                <Icon name="arrow-right" />
              </IconContainer>
            </JustificationContainer>
          </>
        )}
      </Container>
    </>
  );
};

const Container = styled.footer(
  ({ theme }) => css`
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;

    gap: ${theme.space('xxs')};
    padding: 0 ${theme.space('xxs')} ${theme.space('xxs')}
      ${theme.space('base')};

    &:hover ${IconContainer} {
      transform: translate(0.3rem, 0);
    }

    p {
      color: ${theme.color('primary', 'text')};
    }
  `,
);

const AmountContainer = styled(JustificationContainer)(
  ({ theme }) => css`
    margin: 0;
    padding: 0;
    background-color: ${theme.color('secondary')};
    color: ${theme.color('secondary', 'text')};
    border-radius: 50%;
    width: 1.5rem;
    height: 1.5rem;

    flex-shrink: 0;
  `,
);

const IconContainer = styled.div(
  ({ theme }) => css`
    width: 1rem;
    transform: translate(0rem, 0);
    transition: all 0.2s ease;

    svg {
      color: ${theme.color('secondary', 'text')};
    }
  `,
);

const IndicatorContainer = styled.nav(
  ({ theme }) => css`
    display: flex;
    gap: calc(${theme.space('xxs')} / 2);
  `,
);
const ScrollIndicator = styled.a<{ $active?: boolean }>(
  ({ $active, theme }) => css`
    text-decoration: none;
    width: 0.5rem;
    height: 0.5rem;
    background-color: ${theme.color('primary', 'text')};
    display: block;

    opacity: ${$active ? 1 : 0.5};
    border-radius: 50%;

    &:target {
      background-color: ${theme.color('primary', 'text')};
    }
  `,
);

export default Footer;
